import { create } from 'zustand';
import { StudentNotification, PaginatedResponse } from '../types';
import { ZustandOptimisticAdapter } from '../services/optimistic/ZustandOptimisticAdapter';
import { IOptimisticStore } from '../services/interfaces/IOptimisticStore';
import { OptimisticAction } from '../types/optimistic.types';
import { ReconciliationProtocol } from '../services/optimistic/ReconciliationProtocol';
import apiClient from '../utils/apiClient';
import { API_ENDPOINTS } from '../config/apiEndpoints';
import logger from '../services/loggerService';

interface NotificationState {
  notifications: StudentNotification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  optimistic: IOptimisticStore<NotificationState>;

  /**
   * Reactive signal: the most recent notification pushed via WebSocket.
   * This field is set ONLY by `addNotification` (real-time pushes) and is
   * explicitly excluded from `setNotifications`/`fetchNotifications` (bulk API hydration).
   * The `useNotificationEngine` subscribes to this field to seamlessly inject
   * real-time notifications into the active page view without full-page refetch.
   */
  lastPushedNotification: StudentNotification | null;

  addNotification: (notification: StudentNotification) => void;
  setNotifications: (notifications: StudentNotification[]) => void;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  setUnreadCount: (count: number) => void;
  syncNotificationRead: (notificationId: string) => void;
  syncAllNotificationsRead: () => void;
  fetchNotifications: (signal?: AbortSignal) => Promise<void>;
  reset: () => void;
}

export const useNotificationStore = create<NotificationState>((set, get) => {
  const adapter = new ZustandOptimisticAdapter<NotificationState>(get, set);
  const protocol = new ReconciliationProtocol();

  return {
    notifications: [],
    unreadCount: 0,
    isLoading: false,
    error: null,
    lastPushedNotification: null,
    optimistic: adapter,

    addNotification: (notification) =>
      set((state) => ({
        notifications: [notification, ...state.notifications],
        unreadCount: state.unreadCount + 1,
        lastPushedNotification: notification,
      })),

    setNotifications: (notifications) =>
      set((state) => {
        const validNotifications = Array.isArray(notifications) ? notifications : [];
        return {
          ...state,
          notifications: validNotifications,
          unreadCount: validNotifications.filter((n) => n.status === 'UNREAD').length,
        };
      }),

    setUnreadCount: (count) => set({ unreadCount: count }),

    syncNotificationRead: (notificationId) =>
      set((state) => {
        const updatedNotifications = state.notifications.map((n) =>
          n.id === notificationId ? { ...n, status: 'READ' as const } : n
        );
        return {
          ...state,
          notifications: updatedNotifications,
          unreadCount: updatedNotifications.filter((n) => n.status === 'UNREAD').length,
        };
      }),

    syncAllNotificationsRead: () =>
      set((state) => {
        const updatedNotifications = state.notifications.map((n) => ({ ...n, status: 'READ' as const }));
        return {
          ...state,
          notifications: updatedNotifications,
          unreadCount: 0,
        };
      }),

    reset: () => set({
      notifications: [],
      unreadCount: 0,
      isLoading: false,
      error: null,
      lastPushedNotification: null,
    }),

    fetchNotifications: async (signal?: AbortSignal) => {
      set({ isLoading: true, error: null });
      try {
        // The backend returns a PaginatedResponse envelope: { data: [...], totalCount, page, ... }
        // The WebNetworkService auto-unwraps the outer { success, data } envelope,
        // so apiClient returns the PaginatedResponse object directly.
        const response = await apiClient<PaginatedResponse<StudentNotification>>(
          API_ENDPOINTS.NOTIFICATIONS.GET_ALL,
          { signal }
        );

        // Safely extract the notifications array from the paginated envelope.
        // Fallback chain: if response is already an array (defensive), use it directly;
        // otherwise extract .data from the PaginatedResponse; default to empty array.
        const notifications = Array.isArray(response) ? response : response?.data ?? [];
        get().setNotifications(notifications);
        set({ isLoading: false });
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') {
          logger.debug('Fetch notifications aborted');
          return;
        }
        logger.error('Failed to fetch notifications', err, 'useNotificationStore');
        set({ isLoading: false, error: (err as Error).message });
      }
    },

    markAsRead: async (notificationId: string) => {
      const optimistic = get().optimistic;

      // 1. Define Optimistic Action
      const action: OptimisticAction<NotificationState, string> = {
        type: 'MARK_NOTIFICATION_READ',
        payload: notificationId,
        apply: (state, id) => {
          const newNotifications = state.notifications.filter((n) => n.id !== id);
          return {
            ...state,
            notifications: newNotifications,
            unreadCount: Math.max(0, state.unreadCount - 1),
          };
        },
        rollback: (_, snapshot) => snapshot,
      };

      // 2. Apply Optimistically
      const mutationId = optimistic.applyOptimisticUpdate(action);

      try {
        // 3. Execute Remote Mutation via strictly-typed Endpoint
        const response = await apiClient.post(API_ENDPOINTS.NOTIFICATIONS.MARK_READ(notificationId));

        // 4. Interpret Result via Protocol
        const result = protocol.interpretResponse(response);

        if (result.success) {
          optimistic.commitMutation(mutationId);
          logger.debug('Notification marked read and reconciled', { notificationId });
        } else {
          throw result.error || new Error('Server reconciliation failed');
        }
      } catch (err: unknown) {
        logger.warn('Failed to mark notification read - rolling back state', { notificationId, err });
        optimistic.rollbackMutation(mutationId);
        throw err;
      }
    },

    markAllAsRead: async () => {
      const optimistic = get().optimistic;

      const action: OptimisticAction<NotificationState, void> = {
        type: 'MARK_ALL_NOTIFICATIONS_READ',
        payload: undefined,
        apply: (state) => ({
          ...state,
          notifications: [],
          unreadCount: 0,
        }),
        rollback: (_, snapshot) => snapshot,
      };

      const mutationId = optimistic.applyOptimisticUpdate(action);

      try {
        const response = await apiClient.post(API_ENDPOINTS.NOTIFICATIONS.MARK_ALL_READ);
        const result = protocol.interpretResponse(response);

        if (result.success) {
          optimistic.commitMutation(mutationId);
          logger.debug('All notifications marked read and reconciled');
        } else {
          throw result.error || new Error('Server reconciliation failed');
        }
      } catch (err: unknown) {
        logger.error('Failed to mark all notifications read - rolling back', err);
        optimistic.rollbackMutation(mutationId);
        throw err;
      }
    },
  };
});
